import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import {
  InboxMessage,
  MessageTextFieldPopup,
} from '@core/models/interfaces/storage-data/inbox';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';

/**
 * What the dialog's close result means to the message that opened it:
 * - 'back'    — nothing happened; the message stays as it was (Esc and the
 *               backdrop close with `undefined`, which reads the same);
 * - 'dismiss' — the footer dismiss button;
 * - 'action'  — the footer CTA, for buttons the action handler routes;
 * - { responseValue } — the Save of the text-popup form (Figma 1898-128286,
 *               groups 41/42): the sendActionForLogic answer.
 */
export type MessageModalResult =
  | 'back'
  | 'dismiss'
  | 'action'
  | { responseValue: string };

// The legacy validation contract for the text popup, verbatim
// (inboxMessage.component.js): the backend picks the keyboard, the client
// enforces the matching shape.
function textFieldPattern(popup: MessageTextFieldPopup): RegExp | null {
  switch (popup.keyboardType) {
    case 'decimal':
      return /^\d{0,9}[.]?\d{0,2}$/;
    case 'email':
      return /.+@.+\..+/i;
    case 'number':
      return new RegExp(`^[0-9]{0,${popup.maxSymbolsCount}}$`);
    default:
      return null;
  }
}

@Component({
  selector: 'app-message-modal',
  imports: [
    MatButton,
    MatIcon,
    MatDialogClose,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatError,
  ],
  templateUrl: './message-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './message-modal.component.scss',
})
export class MessageModalComponent implements OnInit {
  private mixpanel = inject(MixpanelService);
  private dialogRef =
    inject<MatDialogRef<MessageModalComponent, MessageModalResult>>(
      MatDialogRef
    );
  public data: { message: InboxMessage; openLogicForm?: boolean } =
    inject(MAT_DIALOG_DATA);

  readonly textPopup = signal<MessageTextFieldPopup | null>(null);
  /**
   * The banner used to size itself from `container.offsetWidth` on every
   * check; under OnPush that ran once, before layout. CSS aspect-ratio keeps
   * the proportion without any change detection at all.
   */
  readonly imageAspectRatio: string | null =
    this.data.message.imageWidth && this.data.message.imageHeight
      ? `${this.data.message.imageWidth} / ${this.data.message.imageHeight}`
      : null;
  readonly responseControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  ngOnInit() {
    this.mixpanel.track(
      'Secondary Inbox View ',
      this.getMessageTrackingProps()
    );
    if (this.data.openLogicForm) this.openTextForm();
  }

  onAction() {
    this.mixpanel.track(
      'Secondary  Inbox CTA ',
      this.getMessageTrackingProps()
    );

    const button = this.data.message.actionButton;
    if (
      button?.actionId === 'sendActionForLogic' &&
      button.actionParams?.hash &&
      button.actionParams?.textFieldPopup
    ) {
      this.openTextForm();
      return;
    }

    this.dialogRef.close('action');
  }

  onClose() {
    this.dialogRef.close('dismiss');
  }

  onFormCancel() {
    // Entered straight in form mode (a bodyless message) there is no content
    // view to fall back to — leave without consequences instead.
    if (this.data.openLogicForm) this.dialogRef.close('back');
    else this.textPopup.set(null);
  }

  onFormSave() {
    this.responseControl.markAsTouched();
    if (this.responseControl.invalid) return;
    this.dialogRef.close({ responseValue: this.responseControl.value });
  }

  private openTextForm() {
    const popup =
      this.data.message.actionButton?.actionParams?.textFieldPopup ?? null;
    if (!popup) return;

    const pattern = textFieldPattern(popup);
    if (pattern) {
      this.responseControl.addValidators(Validators.pattern(pattern));
      this.responseControl.updateValueAndValidity();
    }
    this.textPopup.set(popup);
  }

  private getMessageTrackingProps() {
    const { message } = this.data;
    return {
      'Inbox ID': message?.id,
      'Inbox title': message?.title,
      'Has image': !!message?.imageUrl,
    };
  }
}
